import { SHStack } from "@components/design-systems";
import { SHNumericFieldV2 } from "@components/design-systems/sh-numeric-field-v2";
import { SHSlider } from "@components/design-systems/sh-slider";
import { FieldTypeId } from "@models/configuration";
import { MSFilterItemSize } from "@models/model-screener/enums/filterValues";
import { useTheme } from "@mui/material";
import { generateUUID } from "@utils";
import { isEmpty, isEqual, isNil } from "lodash";
import { forwardRef, useEffect, useMemo, useState } from "react";
import {
  EditMSFilterSliderFieldPath,
  getMSFilterRenderedItemInitialProps,
  MSFilterRenderedItemProps,
  MSFilterSliderInitialProps,
  MSTableFilterItemRef,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import { MSFilterResetIcon } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/reset";
import { useMount } from "react-use";
import { useFormContext } from "react-hook-form";
import { ModelScreenerFilterDTO } from "@models/model-screener/entities/modelScreenerFilter";
import { MSFilterDefaultValueConstants } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { getMSFilterSliderInputSx } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/slider/utils";

export const MSFilterSlider = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(
  (
    {
      item,
      itemSize,
      itemPaths,
      disabled,
      allowOverflow,
    }: MSFilterRenderedItemProps,
    ref,
  ) => {
    const { palette, shadows } = useTheme();
    const isSmallSize = itemSize === MSFilterItemSize.Small;
    const sliderFieldPaths = itemPaths as EditMSFilterSliderFieldPath;
    const {
      label,
      fieldType,
      minValue: minSliderValue,
      maxValue: maxSliderValue,
      stepValue,
    } = getMSFilterRenderedItemInitialProps<MSFilterSliderInitialProps>(item) ||
    {};
    const initialMinValue =
      minSliderValue ?? MSFilterDefaultValueConstants.SliderMinValue;
    const initialMaxValue =
      maxSliderValue ?? MSFilterDefaultValueConstants.SliderMaxValue;

    const { getValues, setValue } = useFormContext<ModelScreenerFilterDTO>();

    const [sliderRange, setSliderRange] = useState<number[]>([]);
    const [minValue, setMinValue] = useState<number>(sliderRange[0]);
    const [maxValue, setMaxValue] = useState<number>(sliderRange[1]);
    const [inputKey, setInputKey] = useState<string>(generateUUID());

    useMount(() => {
      const formMinValue = getValues(sliderFieldPaths.currentMinValue);
      const minValue = formMinValue ? Number(formMinValue) : initialMinValue;
      const formMaxValue = getValues(sliderFieldPaths.currentMaxValue);
      const maxValue = formMaxValue ? Number(formMaxValue) : initialMaxValue;

      setSliderRange([minValue, maxValue]);
      setMinValue(minValue);
      setMaxValue(maxValue);
    });

    const handleSliderValueChange = (
      _: Event,
      value: number | number[],
      activeThumb: number,
    ) => {
      const newValue = Array.isArray(value) ? value : [value, value];
      if (activeThumb === 0) {
        setSliderRange([Math.min(newValue[0], sliderRange[1]), sliderRange[1]]);
        setMinValue(Math.min(newValue[0], sliderRange[1]));
      } else {
        setSliderRange([sliderRange[0], Math.max(newValue[1], sliderRange[0])]);
        setMaxValue(Math.max(newValue[1], sliderRange[0]));
      }
    };

    const resetMinToDefault = () => {
      const newMinValue = allowOverflow
        ? MSFilterDefaultValueConstants.SliderLimitRangeMin
        : initialMinValue;
      setMinValue(newMinValue);
      setSliderRange([newMinValue, maxValue]);
      setInputKey(generateUUID());
    };

    const resetMaxToDefault = () => {
      const newMaxValue = allowOverflow
        ? MSFilterDefaultValueConstants.SliderLimitRangeMax
        : initialMaxValue;
      setMaxValue(newMaxValue);
      setSliderRange([minValue, newMaxValue]);
      setInputKey(generateUUID());
    };

    const updateMinValue = (value: number) => {
      const newMinValue = Math.min(value, maxValue);
      setMinValue(newMinValue);
      setSliderRange([newMinValue, maxValue]);
    };

    const updateMaxValue = (value: number) => {
      const newMaxValue = Math.max(value, minValue);
      setMaxValue(newMaxValue);
      setSliderRange([minValue, newMaxValue]);
    };

    const isValueOutOfBounds = (value: number, lower: number, upper: number): boolean => {
      const [minLimit, maxLimit] = allowOverflow
        ? [MSFilterDefaultValueConstants.SliderLimitRangeMin, MSFilterDefaultValueConstants.SliderLimitRangeMax]
        : [lower, upper];
      return value < minLimit || value > maxLimit;
    };

    const handleOnInputBlur = (value: string, isMinField: boolean) => {
      const floatValue = parseFloat(value);
      if (isNaN(floatValue)) {
        return isMinField ? resetMinToDefault() : resetMaxToDefault();
      }
      const [lowerBound, upperBound] = isMinField
        ? [initialMinValue, maxValue]
        : [minValue, initialMaxValue];
      if (isValueOutOfBounds(floatValue, lowerBound, upperBound)) {
        return isMinField ? resetMinToDefault() : resetMaxToDefault();
      }
      return isMinField ? updateMinValue(floatValue) : updateMaxValue(floatValue);
    };

    const handleOnReset = () => {
      setSliderRange([initialMinValue, initialMaxValue]);
      setMinValue(initialMinValue);
      setMaxValue(initialMaxValue);
    };

    useEffect(() => {
      const currentFormMin = Number(
        getValues(sliderFieldPaths.currentMinValue),
      );
      const currentFormMax = Number(
        getValues(sliderFieldPaths.currentMaxValue),
      );

      if (
        isEmpty(sliderRange) ||
        isEqual(sliderRange, [currentFormMin, currentFormMax])
      ) {
        return;
      }

      setValue(sliderFieldPaths.currentMinValue, String(sliderRange[0]));
      setValue(sliderFieldPaths.currentMaxValue, String(sliderRange[1]));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliderRange]);

    const shouldShowResetButton = useMemo(() => {
      return !isEqual(sliderRange, [initialMinValue, initialMaxValue]);
    }, [initialMaxValue, initialMinValue, sliderRange]);

    const renderInputField = (value: number, isMinField: boolean) => {
      const suffix = [
        FieldTypeId.Percentage,
        FieldTypeId.PercentageText,
      ].includes(fieldType as FieldTypeId)
        ? "%"
        : "";

      return (
        <SHStack
          width={isSmallSize ? 55 : 65}
          height={30}
          borderRadius={3}
          bgcolor={
            disabled
              ? palette.action.disabledBackground
              : palette.common.lightSteelBlue
          }
          justifyItems={"center"}
          alignContent={"center"}
          alignItems={"center"}
        >
          <SHStack width={isSmallSize ? 40 : 60}>
            <SHNumericFieldV2
              disabled={disabled}
              key={inputKey}
              value={value}
              decimalScale={2}
              suffix={suffix}
              sx={getMSFilterSliderInputSx(isSmallSize)}
              onBlur={(e) => {
                handleOnInputBlur(e.target.value, isMinField);
              }}
            />
          </SHStack>
        </SHStack>
      );
    };

    if (isNil(minSliderValue) || isNil(maxSliderValue) || isNil(stepValue)) {
      return null;
    }

    return (
      <SHStack
        ref={ref}
        padding={isSmallSize ? 1 : 1.5}
        bgcolor={palette.common.white}
        borderRadius={"12px"}
        boxShadow={shadows[1]}
      >
        <SHStack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <MSFilterItemLabel size={itemSize} disabled={disabled}>
            {label}
          </MSFilterItemLabel>

          {shouldShowResetButton && (
            <MSFilterResetIcon onClick={handleOnReset} disabled={disabled} />
          )}
        </SHStack>
        <SHStack justifyContent={"center"} alignItems={"center"} paddingX={0.5}>
          <SHSlider
            sx={{
              color: disabled ? palette.text.disabled : palette.primary.main,
            "& .MuiSlider-thumb::before": {
              pointerEvents: "all",
            },
            }}
            size={
              isSmallSize ? MSFilterItemSize.Small : MSFilterItemSize.Medium
            }
            min={minSliderValue}
            max={maxSliderValue}
            value={sliderRange}
            step={stepValue}
            disabled={disabled}
            disableSwap
            onChange={handleSliderValueChange}
          />
        </SHStack>
        <SHStack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {renderInputField(minValue, true)}
          <MSFilterItemLabel size={itemSize} disabled={disabled}>
            to
          </MSFilterItemLabel>
          {renderInputField(maxValue, false)}
        </SHStack>
      </SHStack>
    );
  },
);
